import type { Todo } from "../../domain/entities.ts";

export interface TaskRowSwipeCapabilities<Snapshot, Field extends string> {
  enabled(): boolean;
  selectionMode(): boolean;
  tasks(): Todo[];
  rootId(): string | null;
  canEdit(id: string): boolean;
  gestureStartsOnControl(target: Element): boolean;
  blockers(task: Todo): Todo[];
  mutate(id: string, update: (task: Todo) => void): Promise<unknown>;
  complete(task: Todo): Promise<string | null>;
  snapshot(task: Todo): Snapshot;
  undo(message: string, rootId: string, snapshots: Snapshot[], fields: Field[], generatedIds?: string[]): void;
  toast(message: string, error?: boolean): void;
  hapticFeedback(): void;
  render(): Promise<void>;
  sync(): void;
}

export function bindTaskRowSwipes<Snapshot, Field extends string>(root: ParentNode, capabilities: TaskRowSwipeCapabilities<Snapshot, Field>): void {
  if (!capabilities.enabled() || !window.matchMedia("(pointer: coarse)").matches || capabilities.selectionMode()) return;
  root.querySelectorAll<HTMLElement>(".todo-row").forEach(row => {
    let startX = 0; let startY = 0; let active = false; let delta = 0;
    row.addEventListener("touchstart", event => {
      const touch = event.touches[0]; if (!touch || touch.clientX < 96 || touch.clientX > window.innerWidth - 28 || capabilities.gestureStartsOnControl(event.target as Element)) return;
      startX = touch.clientX; startY = touch.clientY; active = true;
    }, { passive: true });
    row.addEventListener("touchmove", event => {
      if (!active) return; const touch = event.touches[0]; delta = touch.clientX - startX;
      if (Math.abs(touch.clientY - startY) > Math.abs(delta)) { active = false; return; }
      if (Math.abs(delta) < 8) return; event.preventDefault(); const distance = Math.max(-96, Math.min(96, delta));
      row.style.transform = `translate3d(${distance}px,0,0)`; row.classList.toggle("is-swipe-complete", distance > 0); row.classList.toggle("is-swipe-pin", distance < 0);
    }, { passive: false });
    row.addEventListener("touchend", async () => {
      if (!active) return; active = false; row.style.transform = ""; row.classList.remove("is-swipe-complete", "is-swipe-pin");
      if (Math.abs(delta) < 64) { delta = 0; return; }
      row.dataset.swiped = "true"; const id = row.dataset.todo!; const todo = capabilities.tasks().find(item => item.id === id); const rootId = capabilities.rootId();
      if (!todo || !rootId || !capabilities.canEdit(id)) return;
      const before = capabilities.snapshot(todo); const completing = delta > 0; let generatedId: string | null = null;
      if (completing) {
        if (!todo.completed && capabilities.blockers(todo).length) { capabilities.toast("Complete this task’s blockers first", true); return; }
        if (todo.completed) await capabilities.mutate(id, draft => { draft.completed = false; draft.completedAt = null; }); else generatedId = await capabilities.complete(todo);
      } else await capabilities.mutate(id, draft => { draft.pinned = !draft.pinned; });
      capabilities.hapticFeedback(); delta = 0; await capabilities.render(); capabilities.sync();
      capabilities.undo(completing ? (todo.completed ? "Task reopened" : "Task completed") : (todo.pinned ? "Task unpinned" : "Task pinned"), rootId, [before], (completing ? ["completed", "completedAt"] : ["pinned"]) as Field[], generatedId ? [generatedId] : []);
    });
  });
}
